import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Button, Spin, Alert, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import axios from 'axios';
import { BASE_API } from '../../axios';
import { TokenStorage } from '../../utils/cookieStorage';
import {
  applyTemplateToDocument,
  createDocument,
  deleteDocument,
  downloadDocumentBlob,
  forceSaveDocument,
  getEditorConfig,
} from '../OnlyOfficeDocuments/documentsApi';
import useOnlyOfficeScript from '../OnlyOfficeDocuments/useOnlyOfficeScript';

const EDITOR_CONTAINER_ID = 'oo-template-content-editor';

interface TemplateContentModalProps {
  visible: boolean;
  templateData: any;
  mode: 'view' | 'edit';
  onClose: () => void;
  onSuccess?: () => void;
}

const TemplateContentModal: React.FC<TemplateContentModalProps> = ({
  visible,
  templateData,
  mode,
  onClose,
  onSuccess,
}) => {
  const [tempDocId, setTempDocId] = useState<number | null>(null);
  const [editorConfig, setEditorConfig] = useState<any>(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState<string | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const editorInstanceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tempDocIdRef = useRef<number | null>(null);

  const { loaded: scriptLoaded, error: scriptError, retry: retryScript } = useOnlyOfficeScript(onlyOfficeUrl);

  // Create temp document from template and fetch editor config
  useEffect(() => {
    if (!visible || !templateData) return;

    let cancelled = false;
    setEditorLoading(true);
    setEditorError(null);
    setTempDocId(null);
    setEditorConfig(null);
    setOnlyOfficeUrl(null);

    const title = `_tmpl_${mode}_${templateData.dt_id}_${Date.now()}`;

    createDocument({ title })
      .then(async (res) => {
        if (cancelled) return;
        const docId = res.data?.data?.id || res.data?.data?.doc_id;
        if (!docId) throw new Error('Failed to create temporary document');

        setTempDocId(docId);
        tempDocIdRef.current = docId;

        // Populate the document with the template's content
        await applyTemplateToDocument(docId, templateData.dt_id);
        if (cancelled) return;

        const configRes = await getEditorConfig(docId, mode);
        if (cancelled) return;

        let { config, onlyOfficeUrl: url } = configRes.data.data;

        if (mode === 'view') {
          config = {
            ...config,
            editorConfig: { ...config.editorConfig, mode: 'view' },
          };
        }

        setEditorConfig(config);
        setOnlyOfficeUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        setEditorError(err.response?.data?.message || err.message || 'Failed to load template content');
        setEditorLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, templateData, mode]);

  // Cleanup on unmount
  useEffect(() => () => cleanupEditor(), []);

  // Initialize OnlyOffice editor once script + config are ready
  useEffect(() => {
    if (!scriptLoaded || !editorConfig || !containerRef.current) return;
    if (!visible) return;

    const timer = setTimeout(() => {
      try {
        // Destroy previous instance if any
        if (editorInstanceRef.current) {
          try { editorInstanceRef.current.destroyEditor(); } catch {}
          editorInstanceRef.current = null;
        }

        editorInstanceRef.current = new (window as any).DocsAPI.DocEditor(
          EDITOR_CONTAINER_ID,
          {
            ...editorConfig,
            events: {
              ...editorConfig.events,
              onDocumentReady: () => setEditorLoading(false),
              onError: (event: any) => {
                console.error('OnlyOffice editor error:', event);
                setEditorError('Editor encountered an error. Please try again.');
                setEditorLoading(false);
              },
            },
          }
        );
      } catch (err) {
        console.error('Failed to initialize OnlyOffice editor:', err);
        setEditorError('Failed to initialize editor');
        setEditorLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scriptLoaded, editorConfig, visible]);

  const cleanupEditor = useCallback(() => {
    if (editorInstanceRef.current) {
      try { editorInstanceRef.current.destroyEditor(); } catch {}
      editorInstanceRef.current = null;
    }
    const docId = tempDocIdRef.current;
    if (docId) {
      deleteDocument(docId).catch(() => {});
      tempDocIdRef.current = null;
    }
    setTempDocId(null);
    setEditorConfig(null);
    setOnlyOfficeUrl(null);
    setEditorError(null);
    setEditorLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    cleanupEditor();
    onClose();
  }, [cleanupEditor, onClose]);

  const handleSaveContent = useCallback(async () => {
    if (!tempDocId || !templateData) return;

    setSaving(true);
    try {
      await forceSaveDocument(tempDocId);
      // Wait for OnlyOffice to process the callback and save the file
      await new Promise(resolve => setTimeout(resolve, 3000));

      const blob = await downloadDocumentBlob(tempDocId);

      const formData = new FormData();
      formData.append('dt_id', String(templateData.dt_id));
      formData.append('dt_template_name', templateData.dt_template_name);
      formData.append('dt_display_name', templateData.dt_display_name);
      formData.append('dt_modality', templateData.dt_modality);
      formData.append('dt_bodypart', templateData.dt_bodypart);
      if (templateData.dt_comments) formData.append('dt_comments', templateData.dt_comments);
      formData.append('dt_active', templateData.dt_active || 'Y');
      formData.append('file', blob, `${templateData.dt_template_name || 'template'}.docx`);

      const token = TokenStorage.getAccessToken();
      const csrfToken = sessionStorage.getItem('csrf_token');
      const res = await axios.post(`${BASE_API}/update-template-oodocs`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
        },
        timeout: 60000,
      });

      if (res.data?.success) {
        message.success('Template content updated successfully');
        cleanupEditor();
        onSuccess?.();
        onClose();
      } else {
        message.error(res.data?.message || 'Failed to save template content');
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || err.message || 'Failed to save template content');
    } finally {
      setSaving(false);
    }
  }, [tempDocId, templateData, cleanupEditor, onSuccess, onClose]);

  const title = templateData
    ? `${mode === 'view' ? 'View' : 'Edit'} Template Content — ${templateData.dt_display_name || templateData.dt_template_name}`
    : mode === 'view' ? 'View Template Content' : 'Edit Template Content';

  const footer = mode === 'edit'
    ? [
        <Button key="cancel" onClick={handleClose} disabled={saving}>Cancel</Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSaveContent}
          loading={saving}
          disabled={!tempDocId || !!editorError}
        >
          Save Content
        </Button>,
      ]
    : [
        <Button key="close" onClick={handleClose}>Close</Button>,
      ];

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleClose}
      width="90vw"
      style={{ top: 20 }}
      styles={{ body: { padding: '12px 16px', height: 'calc(90vh - 120px)' } }}
      footer={footer}
      destroyOnHidden
      maskClosable={false}
    >
      {editorError ? (
        <Alert
          type="error"
          message="Editor Error"
          description={editorError}
          action={
            <Button
              onClick={() => {
                setEditorError(null);
                setTempDocId(null);
                tempDocIdRef.current = null;
                setEditorConfig(null);
              }}
            >
              Retry
            </Button>
          }
        />
      ) : scriptError ? (
        <Alert
          type="error"
          message="Failed to load editor script"
          description={scriptError}
          action={<Button onClick={retryScript}>Retry</Button>}
        />
      ) : (
        <div style={{ position: 'relative', height: '100%' }}>
          {editorLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.8)',
              }}
            >
              <Spin size="large" tip="Loading template content..." />
            </div>
          )}
          <div
            id={EDITOR_CONTAINER_ID}
            ref={containerRef}
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      )}
    </Modal>
  );
};

export default TemplateContentModal;
